<template>
    <div class="min-h-screen w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="container mx-auto">
            <div class="px-4 sm:px-8 flex flex-col w-full pb-4 sm:pb-8 mt-4 sm:mt-8">
                <div class="mb-4">
                    <bread-crumb :items="breadCrumbItems"></bread-crumb>
                </div>

                <div v-if="statsLoading" class="flex justify-center py-12">
                    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                </div>

                <template v-else>
                    <!-- Summary Cards -->
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div class="bg-white p-4 shadow rounded-lg">
                            <h2 class="text-sm font-medium text-gray-500">
                                Total {{ pageTitle }}
                            </h2>
                            <p class="text-2xl font-semibold text-gray-800">{{ totalCount }}</p>
                        </div>
                        <div v-for="(count, program) in categoryCounts" :key="program"
                            class="bg-white p-4 shadow rounded-lg">
                            <h2 class="text-sm font-medium text-gray-500 truncate" :title="program">
                                {{ program }}
                            </h2>
                            <p class="text-2xl font-semibold text-gray-800">{{ count }}</p>
                        </div>
                    </div>

                    <!-- Charts -->
                    <div v-if="hasChartData" class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div class="bg-white p-4 shadow rounded-lg">
                            <h2 class="text-lg font-semibold text-gray-700 mb-2">
                                Department Distribution
                            </h2>
                            <apexchart type="donut" height="300" :options="donutOptions" :series="donutSeries" />
                        </div>
                        <div class="bg-white p-4 shadow rounded-lg">
                            <h2 class="text-lg font-semibold text-gray-700 mb-2">
                                {{ chartLabel }}
                            </h2>
                            <apexchart type="bar" height="300" :options="barOptions" :series="barSeries" />
                        </div>
                    </div>

                    <!-- List -->
                    <section class="mt-8 h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-4 sm:p-6 md:p-8">
                        <div class="faculty-toolbar">
                            <div class="faculty-toolbar__top">
                                <h1 class="faculty-toolbar__title text-xl sm:text-2xl font-semibold text-gray-800 flex items-center">
                                    <font-awesome-icon :icon="pageIcon" class="mr-2" :class="pageIconClass" />
                                    {{ pageTitle }}
                                </h1>
                                <button
                                    v-if="canManageFaculty"
                                    type="button"
                                    @click="openCreateModal"
                                    class="faculty-toolbar__primary"
                                >
                                    <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                                    Add {{ pageTitleSingular }}
                                </button>
                            </div>

                            <div class="faculty-toolbar__panel">
                                <div class="faculty-toolbar__filters">
                                    <div class="faculty-toolbar__filter">
                                        <span class="faculty-toolbar__filter-label">Department</span>
                                        <select
                                            v-model="filterByProgram"
                                            class="faculty-toolbar__select"
                                            @change="onProgramFilterChange"
                                        >
                                            <option value="">All</option>
                                            <option
                                                v-for="(program, index) in programs"
                                                :key="index"
                                                :value="program"
                                            >
                                                {{ program }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="faculty-toolbar__search">
                                        <search
                                            ref="searchRef"
                                            @on-search="onSearch"
                                            class-list="px-3 h-9"
                                        ></search>
                                    </div>
                                </div>

                                <div class="faculty-toolbar__utils">
                                    <button
                                        type="button"
                                        :disabled="isExportExcel"
                                        @click="generateReport"
                                        class="faculty-toolbar__util faculty-toolbar__util--green"
                                    >
                                        <font-awesome-icon :icon="['fas', 'file-excel']" />
                                        <span>{{ isExportExcel ? "Exporting..." : "Export Excel" }}</span>
                                    </button>
                                    <button
                                        v-if="canManageFaculty"
                                        type="button"
                                        @click="downloadBulkTemplate"
                                        class="faculty-toolbar__util faculty-toolbar__util--outline"
                                    >
                                        <font-awesome-icon :icon="['fas', 'download']" />
                                        <span>Download Template</span>
                                    </button>
                                    <button
                                        v-if="canManageFaculty"
                                        type="button"
                                        :disabled="bulkUploading"
                                        @click="openBulkUploadModal"
                                        class="faculty-toolbar__util faculty-toolbar__util--indigo"
                                    >
                                        <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" />
                                        <span>Bulk Upload</span>
                                    </button>
                                </div>
                            </div>

                            <input
                                ref="bulkFileInput"
                                type="file"
                                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                style="display: none"
                                @change="onBulkFileSelected"
                            />
                        </div>

                        <div v-if="listLoading" class="flex justify-center py-10">
                            <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
                        </div>

                        <template v-else>
                            <div class="overflow-x-auto mt-6">
                                <table class="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead class="bg-gray-100 text-gray-600 font-medium">
                                        <tr>
                                            <th class="px-4 py-3 text-left w-12">#</th>
                                            <th class="px-4 py-3 text-left">Name</th>
                                            <th class="px-4 py-3 text-left">Job Title</th>
                                            <th class="px-4 py-3 text-left">E-Mail Address</th>
                                            <th class="px-4 py-3 text-left">Department</th>
                                            <th v-if="canManageFaculty" class="px-4 py-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-100 text-gray-700">
                                        <tr v-for="(item, index) in faculties" :key="item.id || index"
                                            class="hover:bg-blue-50 transition duration-200">
                                            <td class="px-4 py-3 font-medium text-gray-500">
                                                {{ rowNumber(index) }}
                                            </td>
                                            <td class="px-4 py-3">{{ item.name || "—" }}</td>
                                            <td class="px-4 py-3">{{ item.job_title || "—" }}</td>
                                            <td class="px-4 py-3 text-blue-700">
                                                <a v-if="item.email" :href="'mailto:' + item.email"
                                                    class="hover:underline">
                                                    {{ item.email }}
                                                </a>
                                                <span v-else class="text-gray-400 italic">—</span>
                                            </td>
                                            <td class="px-4 py-3">{{ item.program || "—" }}</td>
                                            <td v-if="canManageFaculty" class="px-4 py-3 text-right whitespace-nowrap">
                                                <button type="button"
                                                    class="text-blue-600 hover:text-blue-800 font-medium mr-3"
                                                    @click="openEditModal(item)">
                                                    Edit
                                                </button>
                                                <button type="button"
                                                    class="text-red-600 hover:text-red-800 font-medium"
                                                    @click="confirmDelete(item)">
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                        <tr v-if="!faculties.length">
                                            <td :colspan="canManageFaculty ? 6 : 5"
                                                class="px-4 py-8 text-center text-gray-500">
                                                No {{ pageTitle.toLowerCase() }} found for this campus.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="mt-4" v-if="paginatorInfo && paginatorInfo.total">
                                <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                            </div>
                        </template>
                    </section>
                </template>
            </div>
        </div>

        <!-- Bulk Upload Modal -->
        <transition name="fade">
            <base-modal v-if="showBulkUploadModal" :title="'Bulk Upload ' + pageTitle" @close="closeBulkUploadModal">
                <div class="px-6 pb-6 pt-2 faculty-bulk">
                    <div class="faculty-bulk__banner">
                        <div class="faculty-bulk__banner-icon">
                            <font-awesome-icon :icon="['fas', 'file-excel']" />
                        </div>
                        <div class="faculty-bulk__banner-body">
                            <p class="faculty-bulk__banner-title">
                                Upload {{ pageTitleSingular.toLowerCase() }} records for
                                {{ campusTitle }}
                            </p>
                            <p class="faculty-bulk__banner-meta">
                                Entity <strong>{{ bulkEntityLabel }}</strong>
                                · Campus <strong>{{ bulkCampusLabel }}</strong>
                                · Type <strong>{{ bulkFacultyTypeLabel }}</strong>
                            </p>
                            <p class="faculty-bulk__banner-hint">
                                These values are taken from the current page and are not required in the Excel file.
                            </p>
                        </div>
                    </div>

                    <div v-if="bulkStep === 'select'" class="mt-5 space-y-4">
                        <div class="faculty-bulk__dropzone" :class="{ 'faculty-bulk__dropzone--active': bulkDragOver }"
                            @click="triggerBulkFilePicker" @dragover.prevent="bulkDragOver = true"
                            @dragleave.prevent="bulkDragOver = false" @drop.prevent="onBulkFileDrop">
                            <font-awesome-icon :icon="['fas', 'cloud-upload-alt']"
                                class="faculty-bulk__dropzone-icon" />
                            <p class="faculty-bulk__dropzone-title">
                                Drop your Excel file here, or click to browse
                            </p>
                            <p class="faculty-bulk__dropzone-sub">
                                Accepted formats: .xlsx, .xls
                            </p>
                        </div>

                        <div class="faculty-bulk__tips">
                            <p class="faculty-bulk__tips-title">Before you upload</p>
                            <ul>
                                <li>Download the template and keep the column headers unchanged.</li>
                                <li>
                                    Required columns:
                                    <strong>NAME</strong>,
                                    <strong>EMAIL</strong>,
                                    <strong>JOB TITLE</strong>,
                                    <strong>DEPARTMENT</strong>.
                                </li>
                                <li>
                                    <strong>Do you want to create an account…</strong>: Yes / No.
                                    Yes uses password <code>123456</code> and role <code>faculty</code>.
                                </li>
                                <li>Remove the sample row before uploading real data.</li>
                            </ul>
                            <button type="button" class="faculty-bulk__link-btn" @click="downloadBulkTemplate">
                                <font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
                                Download Excel template
                            </button>
                        </div>
                    </div>

                    <div v-else-if="bulkStep === 'review'" class="mt-5 space-y-4">
                        <div class="faculty-bulk__file-card">
                            <div class="faculty-bulk__file-icon">
                                <font-awesome-icon :icon="['fas', 'file-excel']" />
                            </div>
                            <div class="min-w-0 flex-1">
                                <p class="faculty-bulk__file-name">{{ bulkFileName }}</p>
                                <p class="faculty-bulk__file-meta">
                                    {{ bulkValidRows.length }} valid record{{ bulkValidRows.length === 1 ? '' : 's' }}
                                    <span v-if="bulkValidationErrors.length">
                                        · {{ bulkValidationErrors.length }} validation issue{{
                                        bulkValidationErrors.length === 1
                                        ? '' : 's' }}
                                    </span>
                                </p>
                            </div>
                            <button type="button" class="faculty-bulk__ghost-btn" :disabled="bulkUploading"
                                @click="resetBulkSelection">
                                Change file
                            </button>
                        </div>

                        <div v-if="bulkValidationErrors.length" class="faculty-bulk__errors">
                            <div class="faculty-bulk__errors-head">
                                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                                Fix the following before upload
                            </div>
                            <div class="faculty-bulk__errors-list">
                                <div v-for="(err, idx) in bulkValidationErrors" :key="'bulk-err-' + idx"
                                    class="faculty-bulk__error-item">
                                    <span v-if="err.row" class="faculty-bulk__error-row">
                                        Row {{ err.row }}
                                    </span>
                                    <span>{{ err.message }}</span>
                                </div>
                            </div>
                        </div>

                        <div v-else class="faculty-bulk__ready">
                            <font-awesome-icon :icon="['fas', 'check-circle']" class="faculty-bulk__ready-icon" />
                            <div>
                                <p class="faculty-bulk__ready-title">
                                    Ready to upload {{ bulkValidRows.length }} record{{ bulkValidRows.length === 1 ? ''
                                    : 's' }}
                                </p>
                                <p class="faculty-bulk__ready-sub">
                                    All rows passed validation. Accounts will use password
                                    <strong>123456</strong> and role <strong>faculty</strong> when
                                    create account is Yes.
                                </p>
                            </div>
                        </div>

                        <div class="flex justify-end gap-3 pt-1">
                            <button type="button"
                                class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                                :disabled="bulkUploading" @click="closeBulkUploadModal">
                                Cancel
                            </button>
                            <button type="button"
                                class="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                                :disabled="bulkUploading || !bulkValidRows.length || bulkValidationErrors.length > 0"
                                @click="startBulkUpload">
                                Start Upload
                            </button>
                        </div>
                    </div>

                    <div v-else-if="bulkStep === 'uploading'" class="mt-6">
                        <div class="faculty-bulk__progress-card">
                            <div class="flex items-center justify-between mb-2">
                                <p class="text-sm font-semibold text-gray-800">
                                    Uploading {{ pageTitle.toLowerCase() }}…
                                </p>
                                <p class="text-sm font-bold text-indigo-700">
                                    {{ bulkProgressPercent }}%
                                </p>
                            </div>
                            <div class="faculty-bulk__progress-track">
                                <div class="faculty-bulk__progress-fill" :style="{ width: bulkProgressPercent + '%' }">
                                </div>
                            </div>
                            <p class="mt-3 text-sm text-gray-600">
                                {{ bulkProgressCurrent }} / {{ bulkProgressTotal }}
                                <span class="text-gray-400">·</span>
                                {{ bulkProgressPercent }}%
                            </p>
                            <p v-if="bulkCurrentName" class="mt-1 text-xs text-gray-500 truncate">
                                {{ bulkCurrentName }}
                            </p>
                        </div>
                    </div>

                    <div v-else-if="bulkStep === 'done'" class="mt-5 space-y-4">
                        <div class="faculty-bulk__summary">
                            <div class="faculty-bulk__summary-stat faculty-bulk__summary-stat--ok">
                                <p class="faculty-bulk__summary-value">{{ bulkSuccessCount }}</p>
                                <p class="faculty-bulk__summary-label">Successful</p>
                            </div>
                            <div class="faculty-bulk__summary-stat faculty-bulk__summary-stat--fail">
                                <p class="faculty-bulk__summary-value">{{ bulkFailCount }}</p>
                                <p class="faculty-bulk__summary-label">Failed</p>
                            </div>
                            <div class="faculty-bulk__summary-stat">
                                <p class="faculty-bulk__summary-value">{{ bulkProgressTotal }}</p>
                                <p class="faculty-bulk__summary-label">Total</p>
                            </div>
                        </div>

                        <div v-if="bulkUploadErrors.length" class="faculty-bulk__errors">
                            <div class="faculty-bulk__errors-head">
                                <font-awesome-icon :icon="['fas', 'times-circle']" class="mr-2" />
                                Rows that could not be saved
                            </div>
                            <div class="faculty-bulk__errors-list">
                                <div v-for="(err, idx) in bulkUploadErrors" :key="'bulk-up-err-' + idx"
                                    class="faculty-bulk__error-item">
                                    <span v-if="err.row" class="faculty-bulk__error-row">
                                        Row {{ err.row }}
                                    </span>
                                    <span>{{ err.message }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="flex justify-end gap-3">
                            <button type="button"
                                class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                                @click="resetBulkSelection">
                                Upload another file
                            </button>
                            <button type="button"
                                class="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                                @click="closeBulkUploadModal">
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>

        <!-- Add / Edit Modal -->
        <transition name="fade">
            <base-modal v-if="showFormModal"
                :title="isEditMode ? 'Edit ' + pageTitleSingular : 'Add ' + pageTitleSingular" @close="closeFormModal">
                <div class="px-6 pb-6 pt-2">
                    <form @submit.prevent="submitForm" class="space-y-5">
                        <div v-if="isEditMode"
                            class="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">
                                    Campus <span class="text-red-500">*</span>
                                </label>
                                <select v-model="form.campus" required
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                                    <option v-for="opt in campusSelectOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.label }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">
                                    Faculty type <span class="text-red-500">*</span>
                                </label>
                                <select v-model="form.faculty_type" required
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                                    <option value="admin">Admin</option>
                                    <option value="academic">Academic</option>
                                    <option value="academic_support">Academic Support</option>
                                </select>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">Staff ID</label>
                                <input v-model="form.staff_id" type="text"
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Optional" />
                            </div>
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">
                                    Name <span class="text-red-500">*</span>
                                </label>
                                <input v-model="form.name" type="text" required
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">
                                    Email <span class="text-red-500">*</span>
                                </label>
                                <input v-model="form.email" type="email" required autocomplete="off"
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">
                                    Job Title <span class="text-red-500">*</span>
                                </label>
                                <input v-model="form.job_title" type="text" required
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                            <div class="md:col-span-2">
                                <label class="block text-sm font-semibold text-gray-700 mb-1">
                                    Department <span class="text-red-500">*</span>
                                </label>
                                <input v-model="form.department" type="text" required
                                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                        </div>

                        <div v-if="!isEditMode" class="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                            <p class="text-sm font-semibold text-gray-800">
                                Do you want to create an account for this person?
                            </p>
                            <div class="flex flex-wrap gap-4">
                                <label class="inline-flex items-center gap-2 cursor-pointer">
                                    <input v-model="form.create_account" type="radio" :value="false"
                                        class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                                    <span class="text-sm text-gray-700">No</span>
                                </label>
                                <label class="inline-flex items-center gap-2 cursor-pointer">
                                    <input v-model="form.create_account" type="radio" :value="true"
                                        class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                                    <span class="text-sm text-gray-700">Yes</span>
                                </label>
                            </div>

                            <div v-if="form.create_account" class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">
                                        Password <span class="text-red-500">*</span>
                                    </label>
                                    <input v-model="form.password" type="password" minlength="6"
                                        autocomplete="new-password"
                                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                    <p class="text-xs text-gray-500 mt-1">Minimum 6 characters.</p>
                                </div>
                                <div v-if="canChooseAccountRole">
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">
                                        Role <span class="text-red-500">*</span>
                                    </label>
                                    <select v-model="form.role"
                                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                                        <option v-for="r in roleOptions" :key="'role-' + r.name" :value="r.name">
                                            {{ r.display_name || r.name }}
                                        </option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Phone number</label>
                                    <input v-model="form.phone_number" type="tel" autocomplete="tel"
                                        placeholder="Optional"
                                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                </div>
                                <div class="flex items-center gap-3 pt-6 md:pt-8">
                                    <input id="faculty-receive-email" v-model="form.is_receive_email" type="checkbox"
                                        class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                    <label for="faculty-receive-email" class="text-sm font-medium text-gray-700">
                                        Receive email notifications
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div v-if="isEditMode && editingLinkedUser"
                            class="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                            This faculty member is linked to a user account. Updating name/email/job title will also
                            update the
                            linked user.
                        </div>

                        <div class="flex justify-end gap-3 pt-2">
                            <button type="button" @click="closeFormModal"
                                class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50">
                                Cancel
                            </button>
                            <button type="submit" :disabled="formSubmitting"
                                class="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed">
                                <span v-if="formSubmitting">Saving…</span>
                                <span v-else>{{ isEditMode ? 'Update' : 'Create' }}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </base-modal>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import xlsx from "xlsx/dist/xlsx.full.min.js";
import BreadCrumb from "../../components/shared/BreadCrumb.vue";
import Pagination from "../../components/shared/pagination.vue";
import Search from "../../components/shared/search.vue";
import {
    ADPOLY_CAMPUSES,
    ADPOLY_ENTITY,
    getCampusConfig,
    getPeopleTypeConfig,
    isValidCampus,
    isValidPeopleType,
} from "./config";
import {
    FACULTY_BULK_HEADERS,
    FACULTY_BULK_TEMPLATE_FILENAME,
    FACULTY_BULK_TEMPLATE_URL,
    parseAndValidateFacultyBulkRows,
} from "./facultyBulkUpload";

const EXPORT_FACULTIES_QUERY = gql`
    query ExportFaculties(
        $first: Int!
        $page: Int!
        $entity: String
        $campus: String
        $faculty_type: String
        $program: String
        $search: String
    ) {
        faculties(
            first: $first
            page: $page
            entity: $entity
            campus: $campus
            faculty_type: $faculty_type
            program: $program
            search: $search
        ) {
            error
            error_code
            message
            paginatorInfo {
                hasMorePages
                currentPage
                lastPage
                total
            }
            data {
                staff_id
                name
                email
                entity
                campus
                job_title
                program
                faculty_type
            }
        }
    }
`;

const CREATE_FACULTY = gql`
    mutation CreateFaculty($input: CreateFacultyInput!) {
        createFaculty(input: $input) {
            error
            error_code
            message
            data {
                id
            }
        }
    }
`;

const CREATE_FACULTIES_BULK = gql`
    mutation CreateFacultiesBulk($input: CreateFacultiesBulkInput!) {
        createFacultiesBulk(input: $input) {
            error
            error_code
            message
            data {
                success_count
                fail_count
                total
                results {
                    row
                    email
                    name
                    error
                    message
                }
            }
        }
    }
`;

const UPDATE_FACULTY = gql`
    mutation UpdateFaculty($id: ID!, $input: UpdateFacultyInput!) {
        updateFaculty(id: $id, input: $input) {
            error
            error_code
            message
            data {
                id
            }
        }
    }
`;

const DELETE_FACULTY = gql`
    mutation DeleteFaculty($id: ID!) {
        deleteFaculty(id: $id) {
            error
            error_code
            message
        }
    }
`;

function getEmptyFacultyForm() {
    return {
        staff_id: "",
        name: "",
        email: "",
        job_title: "",
        department: "",
        entity: ADPOLY_ENTITY,
        campus: "",
        faculty_type: "",
        create_account: false,
        password: "",
        role: "faculty",
        phone_number: "",
        is_receive_email: false,
    };
}

export default {
    name: "adpoly-faculty",
    components: {
        BreadCrumb,
        Pagination,
        Search,
    },
    props: ["campus", "peopleType"],
    data() {
        return {
            statsLoading: false,
            listLoading: false,
            faculties: [],
            programs: [],
            categoryCounts: {},
            totalCount: 0,
            filterByProgram: "",
            searchText: "",
            first: 10,
            page: 1,
            paginatorInfo: {},
            showFormModal: false,
            isEditMode: false,
            formSubmitting: false,
            editingId: null,
            editingLinkedUser: false,
            roleOptions: [],
            form: getEmptyFacultyForm(),
            isExportExcel: false,
            generatedReport: [],
            showBulkUploadModal: false,
            bulkStep: "select",
            bulkDragOver: false,
            bulkFileName: "",
            bulkValidRows: [],
            bulkValidationErrors: [],
            bulkUploading: false,
            bulkProgressCurrent: 0,
            bulkProgressTotal: 0,
            bulkCurrentName: "",
            bulkSuccessCount: 0,
            bulkFailCount: 0,
            bulkUploadErrors: [],
            bulkProgressTimer: null,
            powerBiColors: [
                "#01B8AA",
                "#374649",
                "#FD625E",
                "#F2C80F",
                "#5F6B6D",
                "#8AD4EB",
                "#FE9666",
                "#A66999",
                "#3599B8",
                "#DFBFBF",
                "#95A5A6",
            ],
        };
    },
    created() {
        this.bootstrap();
    },
    beforeUnmount() {
        this.clearBulkProgressTimer();
    },
    watch: {
        campus: function () {
            this.bootstrap();
        },
        peopleType: function () {
            this.bootstrap();
        },
        searchText: function () {
            this.page = 1;
            this.fetchFaculties();
        },
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions || [];
        },
        canManageFaculty() {
            return this.role === "superadmin" || this.permissions.includes("faculty-index");
        },
        canChooseAccountRole() {
            return this.role === "superadmin";
        },
        campusConfig() {
            return getCampusConfig(this.campus);
        },
        peopleTypeConfig() {
            return getPeopleTypeConfig(this.peopleType);
        },
        campusTitle() {
            return this.campusConfig ? this.campusConfig.title : "";
        },
        campusCode() {
            return this.campusConfig ? this.campusConfig.campusCode : "";
        },
        campusKey() {
            return this.campusConfig ? this.campusConfig.key : "";
        },
        facultyType() {
            return this.peopleTypeConfig ? this.peopleTypeConfig.facultyType : "";
        },
        pageTitle() {
            return this.peopleTypeConfig ? this.peopleTypeConfig.title : "Faculty";
        },
        pageTitleSingular() {
            if (this.peopleTypeConfig && this.peopleTypeConfig.titleSingular) {
                return this.peopleTypeConfig.titleSingular;
            }
            if (this.pageTitle === "Admins") return "Admin";
            if (this.pageTitle === "Academics") return "Academic";
            if (this.pageTitle === "Academic Support") return "Academic Support";
            return "Faculty";
        },
        pageIcon() {
            return this.peopleTypeConfig
                ? this.peopleTypeConfig.icon
                : ["fas", "users"];
        },
        pageIconClass() {
            return this.peopleTypeConfig
                ? this.peopleTypeConfig.iconClass
                : "text-blue-600";
        },
        chartLabel() {
            return this.peopleTypeConfig
                ? this.peopleTypeConfig.chartLabel
                : "By Department";
        },
        hasChartData() {
            return Object.keys(this.categoryCounts).length > 0;
        },
        campusSelectOptions() {
            return Object.keys(ADPOLY_CAMPUSES).map(function (key) {
                const c = ADPOLY_CAMPUSES[key];
                return {
                    value: c.campusCode,
                    label: c.title + " (" + c.campusCode + ")",
                };
            });
        },
        breadCrumbItems() {
            return [
                { label: "Dashboard", path: "/dashboard" },
                { label: "ADPOLY", path: "/adpoly" },
                {
                    label: this.campusTitle || "Campus",
                    path: this.campusKey ? "/adpoly/" + this.campusKey : "/adpoly",
                },
                {
                    label: "Campus",
                    path: this.campusKey
                        ? "/adpoly/" + this.campusKey + "/campus"
                        : "/adpoly",
                },
                {
                    label: "People",
                    path: this.campusKey
                        ? "/adpoly/" + this.campusKey + "/campus/people"
                        : "/adpoly",
                },
                { label: this.pageTitle },
            ];
        },
        donutOptions() {
            const labels = Object.keys(this.categoryCounts);
            return {
                labels: labels,
                legend: { position: "bottom" },
                colors: this.powerBiColors.slice(0, labels.length),
                dataLabels: { enabled: true },
            };
        },
        donutSeries() {
            return Object.keys(this.categoryCounts).map((key) => {
                return this.categoryCounts[key];
            });
        },
        barOptions() {
            const labels = Object.keys(this.categoryCounts);
            return {
                chart: { toolbar: { show: false } },
                xaxis: {
                    categories: labels,
                    labels: { style: { fontSize: "12px" } },
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        distributed: true,
                    },
                },
                colors: this.powerBiColors.slice(0, labels.length),
                dataLabels: { enabled: true },
                legend: { show: false },
            };
        },
        barSeries() {
            return [
                {
                    name: this.pageTitle,
                    data: Object.keys(this.categoryCounts).map((key) => {
                        return this.categoryCounts[key];
                    }),
                },
            ];
        },
        bulkEntityLabel() {
            return ADPOLY_ENTITY;
        },
        bulkCampusLabel() {
            if (this.campusTitle && this.campusCode) {
                return this.campusTitle + " (" + this.campusCode + ")";
            }
            return this.campusCode || "—";
        },
        bulkFacultyTypeLabel() {
            if (this.peopleTypeConfig && this.peopleTypeConfig.titleSingular) {
                return this.peopleTypeConfig.titleSingular;
            }
            return this.facultyType || "—";
        },
        bulkProgressPercent() {
            if (!this.bulkProgressTotal) return 0;
            return Math.round(
                (this.bulkProgressCurrent / this.bulkProgressTotal) * 100
            );
        },
    },
    methods: {
        bootstrap() {
            if (!isValidCampus(this.campus) || !isValidPeopleType(this.peopleType)) {
                this.$router.push({ path: "/notFound" });
                return;
            }

            this.filterByProgram = "";
            this.searchText = "";
            this.page = 1;
            this.faculties = [];
            this.programs = [];
            this.categoryCounts = {};
            this.totalCount = 0;
            this.paginatorInfo = {};
            this.closeFormModal();
            this.closeBulkUploadModal();

            if (this.$refs.searchRef && this.$refs.searchRef.clearInput) {
                this.$refs.searchRef.clearInput();
            }

            this.fetchStats();
            this.fetchPrograms();
            this.fetchFaculties();
        },
        refreshListData() {
            this.fetchStats();
            this.fetchPrograms();
            this.fetchFaculties();
        },
        rowNumber(index) {
            if (this.paginatorInfo && this.paginatorInfo.firstItem) {
                return this.paginatorInfo.firstItem + index;
            }
            return index + 1;
        },
        setPage(page) {
            this.page = page;
            this.fetchFaculties();
        },
        onSearch(searchText) {
            this.searchText = searchText || "";
        },
        onProgramFilterChange() {
            this.page = 1;
            this.fetchFaculties();
        },
        exportFileName() {
            const campusPart = (this.campusTitle || this.campusCode || "Campus")
                .replace(/\s+/g, "_");
            const typePart = (this.pageTitle || "Faculty").replace(/\s+/g, "_");
            return "ADPOLY_" + campusPart + "_" + typePart;
        },
        mapFacultyExportRow(item) {
            return {
                "STAFF ID": item.staff_id || "",
                NAME: item.name || "",
                EMAIL: item.email || "",
                ENTITY: item.entity || "",
                CAMPUS: item.campus || "",
                "JOB TITLE": item.job_title || "",
                DEPARTMENT: item.program || "",
                "FACULTY TYPE": item.faculty_type || "",
            };
        },
        async generateReport() {
            if (this.isExportExcel) return;
            if (!this.campusCode || !this.facultyType) return;

            this.isExportExcel = true;
            this.generatedReport = [];

            try {
                var exportPage = 1;
                var exportLimit = 500;
                var hasMore = true;

                while (hasMore) {
                    var result = await this.fetchForExporting(exportPage, exportLimit);

                    if (!result || result.error) {
                        throw new Error(
                            (result && result.message) || "Failed to fetch records for export."
                        );
                    }

                    var rows = result.data || [];
                    if (rows.length) {
                        this.generatedReport.push.apply(
                            this.generatedReport,
                            rows.map(this.mapFacultyExportRow)
                        );
                    }

                    var paginator = result.paginatorInfo || {};
                    hasMore = !!paginator.hasMorePages;
                    exportPage += 1;

                    // Safety guard against runaway pagination
                    if (exportPage > 100) {
                        break;
                    }
                }

                if (this.generatedReport.length > 0) {
                    this.exportToExcel(this.exportFileName());
                } else {
                    this.showToast("info", "No records to export.");
                }
            } catch (error) {
                console.error("Faculty Excel export failed:", error);
                this.showToast(
                    "error",
                    (error && error.message) || "Failed to export Excel."
                );
            } finally {
                this.generatedReport = [];
                this.isExportExcel = false;
            }
        },
        async fetchForExporting(pageNo, limitSize) {
            try {
                const response = await this.$apollo.query({
                    query: EXPORT_FACULTIES_QUERY,
                    variables: {
                        first: limitSize,
                        page: pageNo,
                        entity: ADPOLY_ENTITY,
                        campus: this.campusCode,
                        faculty_type: this.facultyType,
                        program: this.filterByProgram || null,
                        search: this.searchText || null,
                    },
                    fetchPolicy: "network-only",
                });

                return response.data && response.data.faculties;
            } catch (error) {
                console.error("Export fetch error:", error);
                return {
                    error: true,
                    message: (error && error.message) || "Failed to fetch data",
                };
            }
        },
        exportToExcel(reportName) {
            const worksheet = xlsx.utils.json_to_sheet(this.generatedReport);
            worksheet["!cols"] = [
                { wch: 14 },
                { wch: 28 },
                { wch: 32 },
                { wch: 12 },
                { wch: 10 },
                { wch: 28 },
                { wch: 28 },
                { wch: 14 },
            ];

            const workbook = xlsx.utils.book_new();
            xlsx.utils.book_append_sheet(workbook, worksheet, this.pageTitle || "Faculty");
            xlsx.writeFile(workbook, reportName + ".xlsx");
        },
        downloadBulkTemplate() {
            try {
                var link = document.createElement("a");
                link.href = FACULTY_BULK_TEMPLATE_URL;
                link.download = FACULTY_BULK_TEMPLATE_FILENAME;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                // Fallback: generate template client-side if asset is unavailable
                console.error("Template download failed, generating locally:", error);
                this.generateBulkTemplateLocally();
            }
        },
        generateBulkTemplateLocally() {
            var headers = FACULTY_BULK_HEADERS.slice();
            var sample = [
                "EMP001",
                "Sample Person",
                "sample.person@example.com",
                "Lecturer",
                "Information Technology",
                "No",
            ];
            var worksheet = xlsx.utils.aoa_to_sheet([headers, sample]);
            worksheet["!cols"] = [
                { wch: 14 },
                { wch: 28 },
                { wch: 36 },
                { wch: 28 },
                { wch: 28 },
                { wch: 48 },
            ];
            var workbook = xlsx.utils.book_new();
            xlsx.utils.book_append_sheet(workbook, worksheet, "Faculty");
            xlsx.writeFile(workbook, FACULTY_BULK_TEMPLATE_FILENAME);
        },
        openBulkUploadModal() {
            if (!this.canManageFaculty) return;
            this.resetBulkUploadState();
            this.showBulkUploadModal = true;
        },
        closeBulkUploadModal() {
            if (this.bulkUploading) return;
            this.showBulkUploadModal = false;
            this.resetBulkUploadState();
            if (this.$refs.bulkFileInput) {
                this.$refs.bulkFileInput.value = "";
            }
        },
        resetBulkUploadState() {
            this.clearBulkProgressTimer();
            this.bulkStep = "select";
            this.bulkDragOver = false;
            this.bulkFileName = "";
            this.bulkValidRows = [];
            this.bulkValidationErrors = [];
            this.bulkUploading = false;
            this.bulkProgressCurrent = 0;
            this.bulkProgressTotal = 0;
            this.bulkCurrentName = "";
            this.bulkSuccessCount = 0;
            this.bulkFailCount = 0;
            this.bulkUploadErrors = [];
        },
        resetBulkSelection() {
            if (this.bulkUploading) return;
            this.resetBulkUploadState();
            if (this.$refs.bulkFileInput) {
                this.$refs.bulkFileInput.value = "";
            }
        },
        triggerBulkFilePicker() {
            if (this.bulkUploading) return;
            if (this.$refs.bulkFileInput) {
                this.$refs.bulkFileInput.click();
            }
        },
        onBulkFileDrop(event) {
            this.bulkDragOver = false;
            var files = event && event.dataTransfer && event.dataTransfer.files;
            if (!files || !files.length) return;
            this.processBulkFile(files[0]);
        },
        onBulkFileSelected(event) {
            var files = event && event.target && event.target.files;
            if (!files || !files.length) return;
            this.processBulkFile(files[0]);
        },
        processBulkFile(file) {
            if (!file) return;

            var name = file.name || "upload.xlsx";
            var lower = name.toLowerCase();
            if (
                lower.indexOf(".xlsx") === -1 &&
                lower.indexOf(".xls") === -1
            ) {
                this.showToast("warning", "Please upload a valid Excel file (.xlsx or .xls).");
                return;
            }

            this.bulkFileName = name;
            this.bulkValidationErrors = [];
            this.bulkValidRows = [];
            this.bulkUploadErrors = [];

            var reader = new FileReader();
            var self = this;

            reader.onload = function (e) {
                try {
                    var data = new Uint8Array(e.target.result);
                    var workbook = xlsx.read(data, { type: "array" });
                    var sheetName = workbook.SheetNames[0];
                    if (!sheetName) {
                        self.bulkValidationErrors = [
                            { row: 0, message: "The Excel file has no sheets." },
                        ];
                        self.bulkStep = "review";
                        return;
                    }

                    var sheet = workbook.Sheets[sheetName];
                    var aoa = xlsx.utils.sheet_to_json(sheet, {
                        header: 1,
                        defval: "",
                        blankrows: false,
                        raw: false,
                    });

                    var parsed = parseAndValidateFacultyBulkRows(aoa);
                    self.bulkValidationErrors = parsed.errors || [];
                    self.bulkValidRows = parsed.rows || [];
                    self.bulkStep = "review";
                } catch (error) {
                    console.error("Failed to parse bulk upload Excel:", error);
                    self.bulkValidationErrors = [
                        {
                            row: 0,
                            message:
                                "Could not read the Excel file. Please use the official template.",
                        },
                    ];
                    self.bulkValidRows = [];
                    self.bulkStep = "review";
                }
            };

            reader.onerror = function () {
                self.showToast("error", "Failed to read the selected file.");
            };

            reader.readAsArrayBuffer(file);
        },
        clearBulkProgressTimer() {
            if (this.bulkProgressTimer) {
                clearInterval(this.bulkProgressTimer);
                this.bulkProgressTimer = null;
            }
        },
        startBulkProgressAnimation(total) {
            var self = this;
            this.clearBulkProgressTimer();
            this.bulkProgressTotal = total;
            this.bulkProgressCurrent = 0;
            this.bulkCurrentName = "Submitting " + total + " record(s)...";

            // Soft progress while the single bulk API request is in flight
            var softCap = Math.max(total - 1, Math.floor(total * 0.9));
            var safeTotal = Math.max(total, 1);
            var tickMs = Math.floor(12000 / safeTotal);
            if (tickMs < 80) tickMs = 80;
            if (tickMs > 400) tickMs = 400;

            this.bulkProgressTimer = setInterval(function () {
                if (self.bulkProgressCurrent < softCap) {
                    self.bulkProgressCurrent += 1;
                }
            }, tickMs);
        },
        async startBulkUpload() {
            if (this.bulkUploading) return;
            if (!this.bulkValidRows.length || this.bulkValidationErrors.length) {
                this.showToast(
                    "warning",
                    "Please fix all validation errors before uploading."
                );
                return;
            }
            if (!this.campusCode || !this.facultyType) {
                this.showToast("error", "Campus or faculty type is missing.");
                return;
            }

            this.bulkUploading = true;
            this.bulkStep = "uploading";
            this.bulkSuccessCount = 0;
            this.bulkFailCount = 0;
            this.bulkUploadErrors = [];
            this.startBulkProgressAnimation(this.bulkValidRows.length);

            try {
                var rows = this.bulkValidRows.map(function (row) {
                    return {
                        row: row.excelRow,
                        staff_id: row.staff_id || null,
                        name: row.name,
                        email: row.email,
                        job_title: row.job_title,
                        department: row.department,
                        create_account: !!row.create_account,
                    };
                });

                var response = await this.$apollo.mutate({
                    mutation: CREATE_FACULTIES_BULK,
                    variables: {
                        input: {
                            entity: ADPOLY_ENTITY,
                            campus: this.campusCode,
                            faculty_type: this.facultyType,
                            rows: rows,
                        },
                    },
                });

                var result =
                    response.data && response.data.createFacultiesBulk;
                var data = result && result.data ? result.data : null;

                if (!result || !data) {
                    throw new Error(
                        (result && result.message) ||
                        "Bulk upload failed. Please try again."
                    );
                }

                this.bulkSuccessCount = data.success_count || 0;
                this.bulkFailCount = data.fail_count || 0;
                this.bulkProgressTotal = data.total || rows.length;
                this.bulkProgressCurrent = this.bulkProgressTotal;

                var uploadErrors = [];
                var results = data.results || [];
                for (var i = 0; i < results.length; i++) {
                    var item = results[i];
                    if (!item || !item.error) continue;
                    uploadErrors.push({
                        row: item.row,
                        message:
                            (item.name ? item.name + " — " : "") +
                            (item.message || "Failed to create record."),
                    });
                }
                this.bulkUploadErrors = uploadErrors;

                // Entire request failed with no usable per-row breakdown
                if (
                    result.error &&
                    this.bulkSuccessCount === 0 &&
                    !uploadErrors.length
                ) {
                    throw new Error(
                        result.message || "Bulk upload failed. No records were created."
                    );
                }
            } catch (error) {
                console.error("Faculty bulk upload failed:", error);
                this.bulkSuccessCount = 0;
                this.bulkFailCount = this.bulkValidRows.length;
                this.bulkProgressCurrent = this.bulkProgressTotal;
                this.bulkUploadErrors = [
                    {
                        row: 0,
                        message:
                            (error && error.message) ||
                            "Bulk upload failed. Please try again.",
                    },
                ];
            } finally {
                this.clearBulkProgressTimer();
                this.bulkUploading = false;
                this.bulkCurrentName = "";
                this.bulkStep = "done";
                this.refreshListData();
            }

            if (this.bulkSuccessCount > 0 && this.bulkFailCount === 0) {
                this.showToast(
                    "success",
                    "Successfully uploaded " + this.bulkSuccessCount + " record(s)."
                );
            } else if (this.bulkSuccessCount > 0) {
                this.showToast(
                    "warning",
                    "Uploaded " +
                    this.bulkSuccessCount +
                    " record(s), " +
                    this.bulkFailCount +
                    " failed."
                );
            } else {
                this.showToast(
                    "error",
                    "Bulk upload failed. No records were created."
                );
            }
        },
        buildCategoryCounts(items) {
            const counts = {};

            items.forEach(function (item) {
                const label =
                    (item.program && String(item.program).trim()) || "Other";
                counts[label] = (counts[label] || 0) + 1;
            });

            return counts;
        },
        async openCreateModal() {
            this.isEditMode = false;
            this.editingId = null;
            this.editingLinkedUser = false;
            this.form = getEmptyFacultyForm();
            this.form.entity = ADPOLY_ENTITY;
            this.form.campus = this.campusCode;
            this.form.faculty_type = this.facultyType;
            this.form.role = "faculty";
            this.showFormModal = true;
            if (this.canChooseAccountRole) {
                await this.fetchRoleOptions();
            }
        },
        openEditModal(item) {
            this.isEditMode = true;
            this.editingId = item.id;
            this.editingLinkedUser = !!item.user_id;
            this.form = getEmptyFacultyForm();
            this.form.staff_id = item.staff_id || "";
            this.form.name = item.name || "";
            this.form.email = item.email || "";
            this.form.job_title = item.job_title || "";
            this.form.department = item.program || "";
            this.form.entity = item.entity || ADPOLY_ENTITY;
            this.form.campus = item.campus || this.campusCode;
            this.form.faculty_type = item.faculty_type || this.facultyType;
            this.showFormModal = true;
        },
        closeFormModal() {
            this.showFormModal = false;
            this.isEditMode = false;
            this.editingId = null;
            this.editingLinkedUser = false;
            this.formSubmitting = false;
            this.form = getEmptyFacultyForm();
        },
        async fetchRoleOptions() {
            try {
                const response = await this.$apollo.query({
                    query: gql`
                        query FacultyFormOptions {
                            facultyFormOptions {
                                error
                                error_code
                                message
                                data {
                                    roles {
                                        id
                                        name
                                        display_name
                                    }
                                }
                            }
                        }
                    `,
                    fetchPolicy: "network-only",
                });

                const result = response.data && response.data.facultyFormOptions;
                if (result && !result.error && result.data) {
                    this.roleOptions = result.data.roles || [];
                } else {
                    this.roleOptions = [];
                }

                if (!this.form.role) {
                    this.form.role = "faculty";
                }
            } catch (error) {
                console.error("Failed to fetch faculty form options:", error);
                this.roleOptions = [];
            }
        },
        trimOrNull(value) {
            if (value === null || value === undefined) return null;
            const t = String(value).trim();
            return t === "" ? null : t;
        },
        showToast(icon, title) {
            if (!this.$swal) return;
            this.$swal.fire({
                toast: true,
                position: "top-end",
                icon: icon,
                title: title,
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });
        },
        validateForm() {
            if (!String(this.form.name || "").trim()) {
                return "Name is required.";
            }
            if (!String(this.form.email || "").trim()) {
                return "Email is required.";
            }
            if (!String(this.form.job_title || "").trim()) {
                return "Job title is required.";
            }
            if (!String(this.form.department || "").trim()) {
                return "Department is required.";
            }

            if (!this.isEditMode && this.form.create_account) {
                if (!this.form.password || String(this.form.password).length < 6) {
                    return "Password is required and must be at least 6 characters.";
                }
                if (this.canChooseAccountRole && !String(this.form.role || "").trim()) {
                    return "Role is required when creating an account.";
                }
            }

            if (this.isEditMode) {
                if (!String(this.form.campus || "").trim()) {
                    return "Campus is required.";
                }
                if (!String(this.form.faculty_type || "").trim()) {
                    return "Faculty type is required.";
                }
            }

            return null;
        },
        async submitForm() {
            const validationError = this.validateForm();
            if (validationError) {
                this.showToast("warning", validationError);
                return;
            }

            this.formSubmitting = true;

            try {
                if (this.isEditMode) {
                    await this.submitUpdate();
                } else {
                    await this.submitCreate();
                }
            } catch (error) {
                console.error("Faculty save failed:", error);
                this.showToast(
                    "error",
                    (error && error.message) || "Failed to save faculty record."
                );
            }

            this.formSubmitting = false;
        },
        async submitCreate() {
            const input = {
                staff_id: this.trimOrNull(this.form.staff_id),
                name: String(this.form.name).trim(),
                email: String(this.form.email).trim(),
                job_title: String(this.form.job_title).trim(),
                department: String(this.form.department).trim(),
                entity: ADPOLY_ENTITY,
                campus: this.campusCode,
                faculty_type: this.facultyType,
                create_account: !!this.form.create_account,
                is_receive_email: !!this.form.is_receive_email,
            };

            if (this.form.create_account) {
                input.password = this.form.password;
                input.role = this.canChooseAccountRole
                    ? String(this.form.role).trim()
                    : "faculty";
                input.phone_number = this.trimOrNull(this.form.phone_number);
            } else {
                input.password = null;
                input.role = null;
                input.phone_number = null;
            }

            const response = await this.$apollo.mutate({
                mutation: CREATE_FACULTY,
                variables: { input: input },
            });

            const result = response.data && response.data.createFaculty;
            if (!result || result.error) {
                throw new Error(
                    (result && result.message) || "Failed to create faculty."
                );
            }

            this.showToast(
                "success",
                result.message || "Faculty created successfully."
            );

            this.closeFormModal();
            this.page = 1;
            this.refreshListData();
        },
        async submitUpdate() {
            const input = {
                staff_id: this.trimOrNull(this.form.staff_id),
                name: String(this.form.name).trim(),
                email: String(this.form.email).trim(),
                job_title: String(this.form.job_title).trim(),
                department: String(this.form.department).trim(),
                entity: String(this.form.entity || ADPOLY_ENTITY).trim(),
                campus: String(this.form.campus).trim(),
                faculty_type: String(this.form.faculty_type).trim(),
            };

            const response = await this.$apollo.mutate({
                mutation: UPDATE_FACULTY,
                variables: {
                    id: this.editingId,
                    input: input,
                },
            });

            const result = response.data && response.data.updateFaculty;
            if (!result || result.error) {
                throw new Error(
                    (result && result.message) || "Failed to update faculty."
                );
            }

            this.showToast(
                "success",
                result.message || "Faculty updated successfully."
            );

            this.closeFormModal();
            this.refreshListData();
        },
        async confirmDelete(item) {
            if (!item || !item.id) return;

            if (item.user_id) {
                this.showToast(
                    "warning",
                    "This faculty member is linked to a user account and cannot be deleted."
                );
                return;
            }

            let confirmed = true;
            if (this.$swal) {
                const result = await this.$swal.fire({
                    icon: "warning",
                    title: "Delete faculty?",
                    text: "Are you sure you want to delete " + (item.name || "this record") + "?",
                    showCancelButton: true,
                    confirmButtonText: "Yes, delete",
                    cancelButtonText: "Cancel",
                });
                confirmed = !!(result && result.isConfirmed);
            }

            if (!confirmed) return;

            try {
                const response = await this.$apollo.mutate({
                    mutation: DELETE_FACULTY,
                    variables: { id: item.id },
                });

                const result = response.data && response.data.deleteFaculty;
                if (!result || result.error) {
                    throw new Error(
                        (result && result.message) || "Failed to delete faculty."
                    );
                }

                this.showToast(
                    "success",
                    result.message || "Faculty deleted successfully."
                );

                this.refreshListData();
            } catch (error) {
                console.error("Faculty delete failed:", error);
                this.showToast(
                    "error",
                    (error && error.message) || "Failed to delete faculty record."
                );
            }
        },
        async fetchPrograms() {
            try {
                const response = await this.$apollo.query({
                    query: gql`
                        query GetFacultyPrograms(
                            $entity: String
                            $campus: String
                            $faculty_type: String
                        ) {
                            facultyPrograms(
                                entity: $entity
                                campus: $campus
                                faculty_type: $faculty_type
                            ) {
                                error
                                error_code
                                message
                                data
                            }
                        }
                    `,
                    variables: {
                        entity: ADPOLY_ENTITY,
                        campus: this.campusCode,
                        faculty_type: this.facultyType,
                    },
                    fetchPolicy: "network-only",
                });

                const result = response.data && response.data.facultyPrograms;
                if (result && !result.error) {
                    this.programs = result.data || [];
                }
            } catch (error) {
                console.error("Failed to fetch faculty programs:", error);
            }
        },
        async fetchStats() {
            this.statsLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: gql`
                        query GetFacultyStats(
                            $first: Int!
                            $page: Int!
                            $entity: String
                            $campus: String
                            $faculty_type: String
                        ) {
                            faculties(
                                first: $first
                                page: $page
                                entity: $entity
                                campus: $campus
                                faculty_type: $faculty_type
                            ) {
                                error
                                error_code
                                message
                                paginatorInfo {
                                    total
                                }
                                data {
                                    id
                                    program
                                }
                            }
                        }
                    `,
                    variables: {
                        first: 1000,
                        page: 1,
                        entity: ADPOLY_ENTITY,
                        campus: this.campusCode,
                        faculty_type: this.facultyType,
                    },
                    fetchPolicy: "network-only",
                });

                const result = response.data && response.data.faculties;
                if (!result || result.error) {
                    throw new Error(
                        (result && result.message) || "Failed to fetch faculty stats"
                    );
                }

                this.totalCount =
                    result.paginatorInfo && result.paginatorInfo.total
                        ? result.paginatorInfo.total
                        : 0;
                this.categoryCounts = this.buildCategoryCounts(result.data || []);
            } catch (error) {
                console.error("Failed to fetch faculty stats:", error);
                this.showToast(
                    "error",
                    (error && error.message) || "Failed to load faculty statistics."
                );
            }
            this.statsLoading = false;
        },
        async fetchFaculties() {
            if (!this.campusCode || !this.facultyType) return;

            this.listLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: gql`
                        query GetFaculties(
                            $first: Int!
                            $page: Int!
                            $entity: String
                            $campus: String
                            $faculty_type: String
                            $program: String
                            $search: String
                        ) {
                            faculties(
                                first: $first
                                page: $page
                                entity: $entity
                                campus: $campus
                                faculty_type: $faculty_type
                                program: $program
                                search: $search
                            ) {
                                error
                                error_code
                                message
                                paginatorInfo {
                                    count
                                    currentPage
                                    firstItem
                                    hasMorePages
                                    lastItem
                                    lastPage
                                    perPage
                                    total
                                }
                                data {
                                    id
                                    user_id
                                    staff_id
                                    name
                                    email
                                    entity
                                    campus
                                    job_title
                                    program
                                    faculty_type
                                }
                            }
                        }
                    `,
                    variables: {
                        first: this.first,
                        page: this.page,
                        entity: ADPOLY_ENTITY,
                        campus: this.campusCode,
                        faculty_type: this.facultyType,
                        program: this.filterByProgram || null,
                        search: this.searchText || null,
                    },
                    fetchPolicy: "network-only",
                });

                const result = response.data && response.data.faculties;
                if (!result || result.error) {
                    throw new Error(
                        (result && result.message) || "Failed to fetch faculties"
                    );
                }

                this.faculties = result.data || [];
                this.paginatorInfo = result.paginatorInfo || {};
            } catch (error) {
                console.error("Failed to fetch faculties:", error);
                this.faculties = [];
                this.paginatorInfo = {};
            }
            this.listLoading = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.faculty-toolbar {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
}

.faculty-toolbar__top {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.faculty-toolbar__title {
    margin: 0;
    min-width: 0;
}

.faculty-toolbar__primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 2.5rem;
    padding: 0 1rem;
    border: 0;
    border-radius: 0.65rem;
    background: #2563eb;
    color: #ffffff;
    font-size: 0.875rem;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(37, 99, 235, 0.25);
}

.faculty-toolbar__primary:hover {
    background: #1d4ed8;
}

.faculty-toolbar__panel {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
    padding: 0.85rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.9rem;
    background: #f8fafc;
}

.faculty-toolbar__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    width: 100%;
}

.faculty-toolbar__filter {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1 1 14rem;
    min-width: 0;
    height: 2.5rem;
    padding: 0 0.85rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.65rem;
    background: #ffffff;
}

.faculty-toolbar__filter-label {
    flex-shrink: 0;
    font-size: 0.8rem;
    font-weight: 600;
    color: #6b7280;
}

.faculty-toolbar__select {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font-size: 0.875rem;
    color: #111827;
}

.faculty-toolbar__search {
    flex: 1 1 14rem;
    min-width: 0;
}

.faculty-toolbar__search :deep(.border) {
    background: #ffffff;
    border-color: #e5e7eb;
    border-radius: 0.65rem;
    min-height: 2.5rem;
}

.faculty-toolbar__utils {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    width: 100%;
    padding-top: 0.75rem;
    border-top: 1px solid #e5e7eb;
}

.faculty-toolbar__util {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    height: 2.25rem;
    padding: 0 0.85rem;
    border-radius: 0.6rem;
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.faculty-toolbar__util:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.faculty-toolbar__util--green {
    border: 1px solid #059669;
    background: #059669;
    color: #ffffff;
}

.faculty-toolbar__util--green:hover:not(:disabled) {
    background: #047857;
    border-color: #047857;
}

.faculty-toolbar__util--outline {
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #374151;
}

.faculty-toolbar__util--outline:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
}

.faculty-toolbar__util--indigo {
    border: 1px solid #4f46e5;
    background: #ffffff;
    color: #4338ca;
}

.faculty-toolbar__util--indigo:hover:not(:disabled) {
    background: #eef2ff;
    border-color: #4338ca;
}

@media (max-width: 639px) {
    .faculty-toolbar__primary {
        width: 100%;
    }

    .faculty-toolbar__util {
        flex: 1 1 calc(50% - 0.25rem);
    }
}

@media (min-width: 1024px) {
    .faculty-toolbar__panel {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
    }

    .faculty-toolbar__filters {
        flex: 1 1 auto;
        flex-wrap: nowrap;
        min-width: 0;
    }

    .faculty-toolbar__utils {
        flex: 0 0 auto;
        width: auto;
        padding-top: 0;
        border-top: 0;
        justify-content: flex-end;
    }
}

.faculty-bulk__banner {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    padding: 1rem 1.125rem;
    border-radius: 0.875rem;
    background: linear-gradient(135deg, #eef2ff 0%, #f8fafc 55%, #ecfeff 100%);
    border: 1px solid #c7d2fe;
}

.faculty-bulk__banner-icon {
    flex-shrink: 0;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #059669;
    color: #ffffff;
    font-size: 1.15rem;
}

.faculty-bulk__banner-title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #1f2937;
}

.faculty-bulk__banner-meta {
    margin: 0.35rem 0 0;
    font-size: 0.8rem;
    color: #4b5563;
}

.faculty-bulk__banner-hint {
    margin: 0.4rem 0 0;
    font-size: 0.75rem;
    color: #6b7280;
}

.faculty-bulk__dropzone {
    border: 2px dashed #c7d2fe;
    border-radius: 1rem;
    background: #f8fafc;
    padding: 2rem 1.25rem;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.faculty-bulk__dropzone:hover,
.faculty-bulk__dropzone--active {
    border-color: #6366f1;
    background: #eef2ff;
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.12);
}

.faculty-bulk__dropzone-icon {
    font-size: 1.75rem;
    color: #4f46e5;
    margin-bottom: 0.75rem;
}

.faculty-bulk__dropzone-title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #111827;
}

.faculty-bulk__dropzone-sub {
    margin: 0.35rem 0 0;
    font-size: 0.8rem;
    color: #6b7280;
}

.faculty-bulk__tips {
    border-radius: 0.875rem;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    padding: 1rem 1.125rem;
}

.faculty-bulk__tips-title {
    margin: 0 0 0.5rem;
    font-size: 0.85rem;
    font-weight: 700;
    color: #374151;
}

.faculty-bulk__tips ul {
    margin: 0;
    padding-left: 1.1rem;
    color: #4b5563;
    font-size: 0.8rem;
    line-height: 1.55;
}

.faculty-bulk__tips li+li {
    margin-top: 0.25rem;
}

.faculty-bulk__tips code {
    background: #f3f4f6;
    border-radius: 0.25rem;
    padding: 0.05rem 0.3rem;
    font-size: 0.75rem;
}

.faculty-bulk__link-btn {
    margin-top: 0.85rem;
    display: inline-flex;
    align-items: center;
    color: #047857;
    font-size: 0.8rem;
    font-weight: 600;
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
}

.faculty-bulk__link-btn:hover {
    color: #065f46;
    text-decoration: underline;
}

.faculty-bulk__file-card {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    padding: 0.9rem 1rem;
    border-radius: 0.875rem;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
}

.faculty-bulk__file-icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.65rem;
    background: #d1fae5;
    color: #047857;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.faculty-bulk__file-name {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.faculty-bulk__file-meta {
    margin: 0.2rem 0 0;
    font-size: 0.78rem;
    color: #6b7280;
}

.faculty-bulk__ghost-btn {
    flex-shrink: 0;
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #374151;
    border-radius: 0.5rem;
    padding: 0.4rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
}

.faculty-bulk__ghost-btn:hover {
    background: #f3f4f6;
}

.faculty-bulk__errors {
    border: 1px solid #fecaca;
    background: #fef2f2;
    border-radius: 0.875rem;
    overflow: hidden;
}

.faculty-bulk__errors-head {
    padding: 0.7rem 0.95rem;
    font-size: 0.82rem;
    font-weight: 700;
    color: #991b1b;
    border-bottom: 1px solid #fecaca;
    background: #fee2e2;
}

.faculty-bulk__errors-list {
    max-height: 220px;
    overflow-y: auto;
    padding: 0.5rem 0.75rem;
}

.faculty-bulk__error-item {
    display: flex;
    gap: 0.55rem;
    align-items: flex-start;
    padding: 0.45rem 0.25rem;
    font-size: 0.78rem;
    color: #7f1d1d;
    border-bottom: 1px solid #fee2e2;
}

.faculty-bulk__error-item:last-child {
    border-bottom: 0;
}

.faculty-bulk__error-row {
    flex-shrink: 0;
    min-width: 3.5rem;
    font-weight: 700;
    color: #b91c1c;
}

.faculty-bulk__ready {
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;
    padding: 1rem;
    border-radius: 0.875rem;
    border: 1px solid #a7f3d0;
    background: #ecfdf5;
}

.faculty-bulk__ready-icon {
    color: #059669;
    font-size: 1.25rem;
    margin-top: 0.1rem;
}

.faculty-bulk__ready-title {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 700;
    color: #065f46;
}

.faculty-bulk__ready-sub {
    margin: 0.3rem 0 0;
    font-size: 0.78rem;
    color: #047857;
    line-height: 1.45;
}

.faculty-bulk__progress-card {
    border-radius: 1rem;
    border: 1px solid #e0e7ff;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    padding: 1.25rem 1.15rem 1.35rem;
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.faculty-bulk__progress-track {
    width: 100%;
    height: 0.7rem;
    border-radius: 9999px;
    background: #e5e7eb;
    overflow: hidden;
}

.faculty-bulk__progress-fill {
    height: 100%;
    border-radius: 9999px;
    background: linear-gradient(90deg, #4f46e5 0%, #06b6d4 100%);
    transition: width 0.25s ease;
}

.faculty-bulk__summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
}

.faculty-bulk__summary-stat {
    border-radius: 0.875rem;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    padding: 0.9rem 0.75rem;
    text-align: center;
}

.faculty-bulk__summary-stat--ok {
    border-color: #a7f3d0;
    background: #ecfdf5;
}

.faculty-bulk__summary-stat--fail {
    border-color: #fecaca;
    background: #fef2f2;
}

.faculty-bulk__summary-value {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 700;
    color: #111827;
}

.faculty-bulk__summary-stat--ok .faculty-bulk__summary-value {
    color: #047857;
}

.faculty-bulk__summary-stat--fail .faculty-bulk__summary-value {
    color: #b91c1c;
}

.faculty-bulk__summary-label {
    margin: 0.2rem 0 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
</style>
